import { adminCaptureException } from '@diamond/shared/utils';
import { ErrorComponent } from '@diamond/sol-admin/components';
import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary for Activity Detail Items Table
 * Catches table rendering errors without breaking the entire page
 * Allows other page sections to continue functioning
 * Logs errors to Sentry for monitoring
 */
export class ActivityDetailTableErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(
      'Activity Detail Table Error Boundary caught error:',
      error,
      errorInfo
    );

    // Send error to Sentry for monitoring
    adminCaptureException(
      {
        message: error.message || 'Activity Detail Table Error',
        error: error.name || 'UnknownError',
        stack: error.stack || '',
        componentStack: errorInfo.componentStack || '',
      },
      (scope) => {
        scope.setTags({
          errorBoundary: 'ActivityDetailTableErrorBoundary',
          component: 'activity-detail-items-table',
          errorType: 'table-render-error',
        });
        scope.setContext('error', {
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
        });
        scope.setContext('errorInfo', {
          componentStack: errorInfo.componentStack,
        });
        scope.setLevel('error');
      }
    );
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorComponent
          message="Terjadi kesalahan saat memuat tabel produk"
          subtitle={
            this.state.error?.message ||
            'Tabel tidak dapat ditampilkan. Bagian lain dari halaman ini masih dapat berfungsi normal.'
          }
          buttonTitle="Coba Tampilkan Ulang"
          action={this.handleReset}
          variant="inline"
          showIllustration={false}
        />
      );
    }

    return this.props.children;
  }
}
